import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const MonitoringPanel = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('metrics');
  const [metrics, setMetrics] = useState(null);
  const [errors, setErrors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  const fetchData = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [metricsRes, errorsRes, alertsRes, healthRes, activityRes] = await Promise.all([
        axios.get(`${API}/api/monitoring/metrics?period=${period}`, { headers }),
        axios.get(`${API}/api/monitoring/errors?limit=20`, { headers }),
        axios.get(`${API}/api/monitoring/alerts`, { headers }),
        axios.get(`${API}/api/monitoring/health`, { headers }),
        axios.get(`${API}/api/monitoring/activity?limit=15`, { headers })
      ]);
      
      setMetrics(metricsRes.data);
      setErrors(errorsRes.data.errors || []);
      setAlerts(alertsRes.data.alerts || []);
      setHealth(healthRes.data);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
    } finally {
      setLoading(false);
    }
  }, [user, period]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const resolveError = async (errorId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/api/monitoring/errors/${errorId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setErrors(errors.map(e => e.id === errorId ? { ...e, resolved: true } : e));
    } catch (err) {
      console.error('Error resolving:', err);
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      healthy: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      degraded: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      unhealthy: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full border ${colors[status] || colors.healthy}`}>
        {status === 'healthy' ? '● Sano' : status === 'degraded' ? '◐ Degradado' : '○ Crítico'}
      </span>
    );
  };

  const MetricCard = ({ title, value, subtitle, icon, trend }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400 text-sm">{title}</span>
        <span className="text-xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      {trend !== undefined && (
        <div className={`text-xs mt-1 ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs anterior
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monitoring-panel">
      {/* Alerts Banner */}
      <AnimatePresence>
        {alerts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div className="flex-1">
                <h4 className="font-medium text-amber-300">{alerts.length} Alerta{alerts.length > 1 ? 's' : ''} Activa{alerts.length > 1 ? 's' : ''}</h4>
                <p className="text-sm text-amber-200/70">{alerts[0]?.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header with Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2 bg-slate-800/50 p-1 rounded-lg">
          {[
            { id: 'metrics', label: 'Métricas', icon: '📊' },
            { id: 'errors', label: 'Errores', icon: '🚨' },
            { id: 'health', label: 'Salud', icon: '💚' },
            { id: 'activity', label: 'Actividad', icon: '📋' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id 
                  ? 'bg-cyan-500 text-white' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
        
        {activeTab === 'metrics' && (
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white">
            <option value="24h">Últimas 24h</option>
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
          </select>
        )}
      </div>

      {/* Metrics Tab */}
      {activeTab === 'metrics' && metrics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard title="Llamadas IA" value={metrics.total_ai_calls} icon="🤖" 
              subtitle={`${metrics.successful_ai_calls} exitosas`} />
            <MetricCard title="Tasa de Éxito" value={`${metrics.success_rate}%`} icon="✅"
              subtitle={`${metrics.failed_ai_calls} fallidas`} />
            <MetricCard title="Créditos Usados" value={metrics.credits_consumed.toFixed(1)} icon="💳" />
            <MetricCard title="Tiempo Promedio" value={`${metrics.avg_response_time_ms}ms`} icon="⚡" 
              subtitle={metrics.fallback_usage_count > 0 ? `${metrics.fallback_usage_count} fallbacks` : null} />
          </div>

          {/* Model Breakdown */}
          {Object.keys(metrics.model_breakdown || {}).length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Uso por Modelo</h3>
              <div className="space-y-3">
                {Object.entries(metrics.model_breakdown).map(([model, data]) => (
                  <div key={model} className="flex items-center gap-4">
                    <span className="w-28 text-sm text-slate-400 truncate">{model}</span>
                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                        style={{ width: `${(data.success / Math.max(data.calls, 1)) * 100}%` }} />
                    </div>
                    <span className="text-sm text-white w-20 text-right">{data.calls} llamadas</span>
                    <span className="text-xs text-slate-500 w-16">{data.credits.toFixed(1)} cr</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Errors Tab */}
      {activeTab === 'errors' && (
        <div className="space-y-4">
          {errors.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <span className="text-4xl block mb-2">✨</span>
              ¡Sin errores recientes! Todo funciona correctamente.
            </div>
          ) : (
            errors.map(error => (
              <motion.div key={error.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className={`bg-slate-800/50 border rounded-xl p-4 ${
                  error.resolved ? 'border-slate-700/50 opacity-60' : 'border-red-500/30'
                }`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        error.error_type === 'ai_error' ? 'bg-purple-500/20 text-purple-400' :
                        error.error_type === 'payment_error' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>{error.service}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(error.created_at).toLocaleString('es')}
                      </span>
                      {error.resolved && <span className="text-xs text-emerald-400">✓ Resuelto</span>}
                    </div>
                    <p className="text-sm text-white">{error.error_message}</p>
                  </div>
                  {!error.resolved && (
                    <button onClick={() => resolveError(error.id)}
                      className="px-3 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition">
                      Resolver
                    </button>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Health Tab */}
      {activeTab === 'health' && health && (
        <div className="space-y-6">
          <div className="flex items-center gap-4 p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
              health.overall_status === 'healthy' ? 'bg-emerald-500/20' :
              health.overall_status === 'degraded' ? 'bg-yellow-500/20' : 'bg-red-500/20'
            }`}>
              {health.overall_status === 'healthy' ? '💚' : health.overall_status === 'degraded' ? '💛' : '❤️'}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Estado General del Sistema</h3>
              <StatusBadge status={health.overall_status} />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(health.services || {}).map(([service, data]) => (
              <div key={service} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium capitalize">{service}</span>
                  <StatusBadge status={data.status} />
                </div>
                {data.recent_errors > 0 && (
                  <p className="text-xs text-slate-400">{data.recent_errors} errores (1h)</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-2">
          {activity.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Sin actividad reciente</div>
          ) : (
            activity.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-slate-800/30 rounded-lg">
                <span className="text-xl">
                  {item.type === 'ai_call' ? (item.success ? '🤖' : '❌') :
                   item.type === 'error' ? '🚨' : '🚀'}
                </span>
                <div className="flex-1">
                  <p className="text-sm text-white">
                    {item.type === 'ai_call' ? `Llamada IA (${item.model})` :
                     item.type === 'error' ? `Error: ${item.message}` :
                     `Despliegue: ${item.name}`}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(item.created_at).toLocaleString('es')}
                  </p>
                </div>
                {item.credits && <span className="text-xs text-cyan-400">-{item.credits} cr</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MonitoringPanel;
